import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Auth, authState, signOut } from '@angular/fire/auth';
import { Firestore, doc, serverTimestamp, updateDoc } from '@angular/fire/firestore';
import { take } from 'rxjs/operators';
import { LEGAL_VERSION } from '../../models/user.model';

/**
 * Catch-all screen for owners who are signed in but never accepted the legal
 * terms — the typical case is a brand-new Google user who landed via the
 * login tab (which doesn't show the checkbox). Reuses the exact same legal
 * documents and version as the signup form so the audit trail is consistent.
 */
@Component({
  selector: 'app-accept-terms',
  standalone: true,
  imports: [RouterLink],
  templateUrl: './accept-terms.component.html',
})
export class AcceptTermsComponent implements OnInit {
  private route     = inject(ActivatedRoute);
  private router    = inject(Router);
  private auth      = inject(Auth);
  private firestore = inject(Firestore);

  readonly accepted   = signal(false);
  readonly submitting = signal(false);
  readonly error      = signal<string | null>(null);

  /** Where to send the user after they accept. Defaults to the dashboard. */
  private get returnUrl(): string {
    return this.route.snapshot.queryParamMap.get('returnUrl') ?? '/dashboard';
  }

  ngOnInit(): void {
    // Defensive: if somehow the user lands here without auth, push them back
    // to /login. authGuard already handles this on protected routes, but the
    // accept-terms route only has authGuard (no legalGuard, on purpose).
    authState(this.auth).pipe(take(1)).subscribe(u => {
      if (!u) this.router.navigate(['/login']);
    });
  }

  toggle(): void { this.accepted.update(v => !v); }

  async finalize(): Promise<void> {
    if (!this.accepted() || this.submitting()) return;
    const uid = this.auth.currentUser?.uid;
    if (!uid) { this.router.navigate(['/login']); return; }

    this.submitting.set(true);
    this.error.set(null);
    try {
      await updateDoc(doc(this.firestore, 'users', uid), {
        legalAccepted:   true,
        legalVersion:    LEGAL_VERSION,
        legalAcceptedAt: serverTimestamp(),
      });
      this.router.navigateByUrl(this.returnUrl);
    } catch {
      this.error.set('No se pudo registrar tu aceptación. Inténtalo de nuevo.');
    } finally {
      this.submitting.set(false);
    }
  }

  /** Escape hatch — sign out instead of accepting. */
  async cancel(): Promise<void> {
    await signOut(this.auth);
    this.router.navigate(['/']);
  }
}
